import { Box, Typography } from '@mui/material';
import type { GridColDef, GridFilterOperator } from '@mui/x-data-grid-premium';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { StatusChip } from '@components/StatusChip';
import { StatusFilterInput } from '../components';
import { isOverdue, formatDueDate } from './dateUtils';
import { DownloadCell } from '../components/DownloadCell';

const statusFilterOperators: GridFilterOperator[] = [
  {
    label: 'equals',
    value: 'equals',
    getApplyFilterFn: filterItem => {
      if (!filterItem.value) {
        return null;
      }
      return params => {
        return params.value === filterItem.value;
      };
    },
    InputComponent: StatusFilterInput
  },
  {
    label: 'not equals',
    value: 'not',
    getApplyFilterFn: filterItem => {
      if (!filterItem.value) {
        return null;
      }
      return params => {
        return params.value !== filterItem.value;
      };
    },
    InputComponent: StatusFilterInput
  }
];

export const getWorkOrderColumns = (
  isMobile: boolean,
  isTablet: boolean
): GridColDef[] => {
  if (isMobile) {
    return [
      {
        field: 'mobileView',
        headerName: 'Work Orders',
        width: 350,
        flex: 1,
        minWidth: 320,
        sortable: false,
        filterable: false,
        resizable: false,
        renderCell: params => {
          const row = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                py: 1.5,
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                mt: 0.5
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                >
                  {row.jobNumber}
                </Typography>
                <StatusChip
                  label={row.status}
                  variant="status"
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0
                }}
              >
                <PersonIcon
                  sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {row.customer}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 14, color: 'text.secondary' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: isOverdue(row.expectedDueDate)
                      ? 'error.main'
                      : 'text.secondary',
                    fontWeight: isOverdue(row.expectedDueDate) ? 500 : 400
                  }}
                >
                  Due: {formatDueDate(row.expectedDueDate)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 'auto' }}
                >
                  {row.detailCount} items
                </Typography>
              </Box>
            </Box>
          );
        }
      }
    ];
  }
  if (isTablet) {
    return [
      {
        field: 'jobNumber',
        headerName: 'Job #',
        width: 100,
        flex: 0,
        headerAlign: 'left',
        align: 'left'
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        width: 180,
        flex: 1,
        headerAlign: 'left',
        align: 'left'
      },
      {
        field: 'expectedDueDate',
        headerName: 'Due Date',
        width: 110,
        flex: 0,
        headerAlign: 'left',
        align: 'left',
        valueFormatter: value => formatDueDate(value),
        renderCell: params => (
          <Typography
            variant="body2"
            sx={{
              color: isOverdue(params.value) ? 'error.main' : 'inherit',
              fontWeight: isOverdue(params.value) ? 500 : 400
            }}
          >
            {formatDueDate(params.value)}
          </Typography>
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 100,
        flex: 0,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => (
          <StatusChip
            label={params.value}
            variant="status"
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        ),
        filterOperators: statusFilterOperators
      },
      {
        field: 'detailsCount',
        headerName: 'Items',
        width: 70,
        flex: 0,
        headerAlign: 'center',
        align: 'center'
      },
      {
        field: 'download',
        headerName: '',
        width: 50,
        flex: 0,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => <DownloadCell jobNumber={params.row.jobNumber} />
      }
    ];
  }
  return [
    {
      field: 'jobNumber',
      headerName: 'Job Number',
      minWidth: 175,
      flex: 0.5,
      headerAlign: 'left',
      align: 'left'
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      minWidth: 100,
      flex: 1,
      headerAlign: 'left',
      align: 'left'
    },
    {
      field: 'expectedDueDate',
      headerName: 'Due Date',
      minWidth: 100,
      flex: 0.75,
      headerAlign: 'left',
      align: 'left',
      valueFormatter: value => formatDueDate(value),
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            color: isOverdue(params.value) ? 'error.main' : 'inherit',
            fontWeight: isOverdue(params.value) ? 500 : 400
          }}
        >
          {formatDueDate(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      flex: 0.75,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <StatusChip label={params.value} variant="status" size="small" />
      ),
      filterOperators: statusFilterOperators
    },
    {
      field: 'detailsCount',
      headerName: 'Line Items',
      minWidth: 60,
      flex: 0.5,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'download',
      headerName: 'Offline',
      width: 70,
      flex: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => <DownloadCell jobNumber={params.row.jobNumber} />
    }
  ];
};
